import type { ReactNode } from 'react'

import type { FitMessage, TimeDisplayMode } from '@fit-editor/fit-core'

import { FieldKindTag } from '@/components/data/message-table'
import type { FitDataModel } from '@/components/data/use-fit-data-model'
import { baseTypeName, hasData, humanizeFieldName } from '@/lib/fit-data'
import { useUnits } from '@/hooks/use-units'
import { formatDecodedValue, formatFriendlyValue } from '@/lib/fit-data-format'

export interface FileInfoPanelProps {
  model: FitDataModel
  timeMode: TimeDisplayMode
}

const FILE_ID_ORDER = [
  'type',
  'manufacturer',
  'garminProduct',
  'faveroProduct',
  'product',
  'productName',
  'serialNumber',
  'timeCreated',
  'number',
] as const

/** Hex string for a byte array (e.g. a developer application id), as an RFC 4122-style UUID when it's 16 bytes. */
function formatBytes(value: unknown): string | undefined {
  if (!Array.isArray(value) || !value.every((v) => typeof v === 'number')) return undefined
  const hex = (value as number[]).map((b) => (b & 0xff).toString(16).padStart(2, '0')).join('')
  if (hex.length === 32) {
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
  return hex
}

/**
 * File-level overview: file_id, file_creator, decode problems, per-type
 * message counts, the preservation layer's unknown content, and the
 * developer data definitions.
 */
export function FileInfoPanel({ model, timeMode }: FileInfoPanelProps) {
  const { activity, groups, devFields, profile } = model
  const fileIdMeta = profile?.byMessagesKey.get('fileIdMesgs')
  const creator = activity.otherMessages.fileCreatorMesgs?.[0]
  const creatorMeta = profile?.byMessagesKey.get('fileCreatorMesgs')

  const unknownTypeCount = new Set(
    activity.preservedUnknownMessages.map((m) => m.globalMessageNumber),
  ).size
  const unknownFieldCount = activity.mixedMessageUnknownFields.reduce(
    (sum, entry) => sum + entry.unknownFields.length,
    0,
  )
  const developerDataIds = activity.otherMessages.developerDataIdMesgs ?? []
  const totalMessages = groups.reduce((sum, g) => sum + g.count, 0)

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title="File ID">
        {activity.fileId == null ? (
          <p className="text-muted-foreground text-sm">This file has no file_id message.</p>
        ) : (
          <FieldList
            message={activity.fileId}
            order={FILE_ID_ORDER}
            units={(key) => fileIdMeta?.fieldsByName.get(key)?.units ?? ''}
            timeMode={timeMode}
          />
        )}
      </Section>

      <Section title="File creator">
        {creator == null ? (
          <p className="text-muted-foreground text-sm">No file_creator message in this file.</p>
        ) : (
          <FieldList
            message={creator}
            order={['softwareVersion', 'hardwareVersion']}
            units={(key) => creatorMeta?.fieldsByName.get(key)?.units ?? ''}
            timeMode={timeMode}
          />
        )}
      </Section>

      <Section title="Decode problems">
        {activity.decodeErrors.length === 0 ? (
          <p className="text-muted-foreground text-sm">No decode errors or warnings.</p>
        ) : (
          <ul className="text-warning-text list-disc space-y-1 pl-5 text-sm" role="list">
            {activity.decodeErrors.map((error, i) => (
              <li key={i}>{error.message}</li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Preserved content">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Unknown message types</dt>
          <dd>
            {activity.preservedUnknownMessages.length.toLocaleString()} message
            {activity.preservedUnknownMessages.length === 1 ? '' : 's'} across {unknownTypeCount}{' '}
            type{unknownTypeCount === 1 ? '' : 's'}
          </dd>
          <dt className="text-muted-foreground">Undocumented fields on known messages</dt>
          <dd>
            {unknownFieldCount.toLocaleString()} field{unknownFieldCount === 1 ? '' : 's'} on{' '}
            {activity.mixedMessageUnknownFields.length.toLocaleString()} message
            {activity.mixedMessageUnknownFields.length === 1 ? '' : 's'}
          </dd>
        </dl>
        <p className="text-muted-foreground mt-2 text-xs">
          Content the FIT Profile doesn&apos;t define is kept verbatim and written back on FIT
          export.
        </p>
      </Section>

      <Section title={`Message counts (${totalMessages.toLocaleString()} total)`} wide>
        <div className="border-border overflow-x-auto rounded-md border">
          <table className="w-full border-collapse text-left text-xs">
            <caption className="sr-only">Number of messages of each type</caption>
            <thead className="bg-muted/60">
              <tr>
                <th scope="col" className="px-2 py-1.5 font-semibold">
                  Message
                </th>
                <th scope="col" className="px-2 py-1.5 font-semibold">
                  Global number
                </th>
                <th scope="col" className="px-2 py-1.5 text-right font-semibold">
                  Count
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id} className="border-border border-t">
                  <th scope="row" className="px-2 py-1 font-mono font-normal">
                    <span className="inline-flex items-center gap-1">
                      {group.name}
                      {group.unknownType && <FieldKindTag kind="undocumented" />}
                    </span>
                  </th>
                  <td className="px-2 py-1 tabular-nums">{group.mesgNum ?? '—'}</td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {group.count.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Developer data" wide>
        {developerDataIds.length === 0 && devFields.size === 0 ? (
          <p className="text-muted-foreground text-sm">No developer data in this file.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {developerDataIds.length > 0 && (
              <div className="border-border overflow-x-auto rounded-md border">
                <table className="w-full border-collapse text-left text-xs">
                  <caption className="sr-only">Developer data ids</caption>
                  <thead className="bg-muted/60">
                    <tr>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Developer index
                      </th>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Application id
                      </th>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Application version
                      </th>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Manufacturer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {developerDataIds.map((d, i) => (
                      <tr key={i} className="border-border border-t">
                        <th scope="row" className="px-2 py-1 font-normal tabular-nums">
                          {formatDecodedValue(d.developerDataIndex, timeMode)}
                        </th>
                        <td className="px-2 py-1 font-mono">
                          {formatBytes(d.applicationId) ??
                            formatDecodedValue(d.applicationId, timeMode)}
                        </td>
                        <td className="px-2 py-1">
                          {formatDecodedValue(d.applicationVersion, timeMode)}
                        </td>
                        <td className="px-2 py-1">
                          {formatDecodedValue(d.manufacturerId, timeMode)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {devFields.size > 0 && (
              <div className="border-border overflow-x-auto rounded-md border">
                <table className="w-full border-collapse text-left text-xs">
                  <caption className="sr-only">Developer field descriptions</caption>
                  <thead className="bg-muted/60">
                    <tr>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Field name
                      </th>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Units
                      </th>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Developer index
                      </th>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Field number
                      </th>
                      <th scope="col" className="px-2 py-1.5 font-semibold">
                        Base type
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activity.otherMessages.fieldDescriptionMesgs ?? []).map((fd, i) => {
                      const info = typeof fd.key === 'number' ? devFields.get(fd.key) : undefined
                      return (
                        <tr key={i} className="border-border border-t">
                          <th scope="row" className="px-2 py-1 font-normal">
                            {info?.name ?? formatDecodedValue(fd.fieldName, timeMode)}
                          </th>
                          <td className="px-2 py-1">{info?.units || '—'}</td>
                          <td className="px-2 py-1 tabular-nums">
                            {formatDecodedValue(fd.developerDataIndex, timeMode)}
                          </td>
                          <td className="px-2 py-1 tabular-nums">
                            {formatDecodedValue(fd.fieldDefinitionNumber, timeMode)}
                          </td>
                          <td className="px-2 py-1">
                            {typeof fd.fitBaseTypeId === 'number'
                              ? baseTypeName(fd.fitBaseTypeId)
                              : formatDecodedValue(fd.fitBaseTypeId, timeMode)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Section>
    </div>
  )
}

function Section({
  title,
  wide,
  children,
}: {
  title: string
  wide?: boolean
  children: ReactNode
}) {
  return (
    <section
      className={`border-border bg-card rounded-lg border p-3 ${wide ? 'lg:col-span-2' : ''}`}
    >
      <h3 className="text-card-foreground mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </section>
  )
}

/** A definition list of a message's fields with data: `order` first, then the rest. */
function FieldList({
  message,
  order,
  units,
  timeMode,
}: {
  message: FitMessage
  order: readonly string[]
  units: (key: string) => string
  timeMode: TimeDisplayMode
}) {
  const unitSystem = useUnits()
  const keys = Object.keys(message).filter((k) => k !== 'developerFields' && hasData(message[k]))
  const ordered = [
    ...order.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !order.includes(k)).sort(),
  ]
  if (ordered.length === 0) {
    return <p className="text-muted-foreground text-sm">No fields with data.</p>
  }
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      {ordered.map((key) => (
        <div key={key} className="contents">
          <dt className="text-muted-foreground">
            {/^\d+$/.test(key) ? (
              <span className="inline-flex items-center gap-1">
                field_{key} <FieldKindTag kind="undocumented" />
              </span>
            ) : (
              humanizeFieldName(key)
            )}
          </dt>
          <dd className="tabular-nums">
            {formatFriendlyValue(key, message[key], units(key), timeMode, { unitSystem })}
          </dd>
        </div>
      ))}
    </dl>
  )
}
